import { Component } from '@theme/component';

/**
 * The contact page. One job only: the "ask about this" button inside a quick
 * answer tile pre-fills the message field, the way the prototype does
 * (html-blocks/kontakt.html).
 *
 * Everything else on the page runs without this file. Opening and closing a
 * tile is native <details>. Validating and sending is Shopify's contact form
 * plus the browser. So the section reads and works completely with JavaScript
 * off — this only removes one round of typing.
 *
 * `message` is deliberately NOT in requiredRefs. A ref belongs to the nearest
 * enclosing component (assets/component.js:171), and this one is ours, but it
 * only exists while the form band is rendered. Listing it would throw
 * MissingRefError on connect and leave this.refs unassigned — the failure that
 * cost half a day in lift-buybox.
 *
 * @typedef {object} Refs
 * @property {HTMLTextAreaElement} [message]
 *
 * @extends {Component<Refs>}
 */
class LiftContact extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#syncAskButtons();
  }

  /** The theme editor re-renders the section, which replaces every tile. */
  updatedCallback() {
    super.updatedCallback();
    this.#syncAskButtons();
  }

  /**
   * The buttons are rendered hidden and revealed here. A control that fills a
   * field must not be offered while there is no field to fill, and without
   * this module there is none either.
   */
  #syncAskButtons() {
    const usable = this.refs.message != null;
    for (const button of this.querySelectorAll('[data-lift-ask]')) {
      /** @type {HTMLElement} */ (button).hidden = !usable;
    }
  }

  /**
   * Prefix the message with the tile's own heading, then hand over the cursor.
   *
   * @param {Event} event
   */
  handleAsk(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const button = target.closest('[data-lift-ask]');
    const field = this.refs.message;
    if (!button || !field || !this.contains(button)) return;

    const lead = button.getAttribute('data-lift-ask') ?? '';
    const value = field.value;

    // Compared trimmed on both sides: the lead ends in a space the visitor may
    // already have eaten, and a second click must not stack a second prefix.
    if (value.trim() === '') field.value = lead;
    else if (!value.trimStart().startsWith(lead.trim())) field.value = lead + value;

    field.focus();
    field.setSelectionRange(field.value.length, field.value.length);
  }
}

if (!customElements.get('lift-contact-component')) {
  customElements.define('lift-contact-component', LiftContact);
}
